import React, { useState, useEffect, useMemo, useCallback, memo } from 'react';
import { 
  Table, 
  Card, 
  Badge, 
  Form, 
  InputGroup, 
  Spinner, 
  Alert, 
  Container, 
  Pagination,
  Button,
  Modal,
  Row,
  Col
} from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import Layout from '../components/Layout';
import { useAuth } from '../hooks/useAuth';
import { useRolePermissions } from '../hooks/useRolePermissions';
import { calendarAPI, clearCache } from '../utils/api';
import type { Calendar, CreateCalendarRequest, UpdateCalendarRequest } from '../types/calendar';
import { formatCalendarTimeRange, formatDateTimeInCompanyTimezone } from '../utils/timezone';

// Lightweight icon replacements to reduce bundle size
const CalendarIcon = () => (
  <i className="bi bi-calendar3 me-1"></i>
);





// Memoized Calendar Card Component for Mobile
const CalendarCard = memo(({ calendar, onEdit, canEdit }: {
  calendar: Calendar;
  onEdit: (calendar: Calendar) => void;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'calendars' });
  const { t: tc } = useTranslation('common');
  const { currentCompany } = useAuth();
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    return formatDateTimeInCompanyTimezone(dateString, currentCompany?.timezone || 'UTC');
  }, [currentCompany?.timezone]);

  const formatTime = useCallback((timeString: string) => {
    if (!timeString) return tc('messages.na');
    return timeString.length > 5 ? timeString.substring(0, 5) : timeString;
  }, [tc]);

  const DAY_KEY_MAP: Record<string, string> = useMemo(() => ({
    Sunday: 'sunday', Monday: 'monday', Tuesday: 'tuesday', Wednesday: 'wednesday',
    Thursday: 'thursday', Friday: 'friday', Saturday: 'saturday',
  }), []);

  const translateDay = useCallback((day: string) => t(DAY_KEY_MAP[day] || day), [t, DAY_KEY_MAP]);

  const parseWorkingDays = useCallback((workingDays: string | string[]): string[] => {
    if (Array.isArray(workingDays)) {
      return workingDays;
    }
    if (typeof workingDays === 'string') {
      try {
        const parsed = JSON.parse(workingDays);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  return (
    <Card className="mobile-calendar-card m-3 an-data-card">
      <Card.Body className="an-data-card">
        <div className="d-flex justify-content-between align-items-start mb-3">
          <div className="d-flex align-items-center">
            <CalendarIcon />
            <div>
              <h6 className="mb-1 fw-bold">{renderCellValue(calendar.name)}</h6>
              <small className="text-muted">ID: {renderCellValue(calendar.id)}</small>
            </div>
          </div>
          <div className="d-flex align-items-center gap-2">
            {canEdit && (
              <Button variant="link" size="sm" className="text-primary p-1 text-decoration-none" onClick={() => onEdit(calendar)} title={t('editCalendar')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
            )}
            <Badge bg="primary">{tc('status.active')}</Badge>
          </div>
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('companyIdColumn')}</small>
          <span className="fw-medium">{renderCellValue(calendar.company_id)}</span>
        </div>
        
        <div className="mb-2">
          <small className="text-muted d-block">{t('workHours')}</small>
          <span className="fw-medium">{formatCalendarTimeRange(formatTime(calendar.check_in_time), formatTime(calendar.check_out_time), calendar.spans_next_day, undefined, t('nextDay'), ' - ')}</span>
        </div>

        <div className="mb-2">
          <small className="text-muted d-block">{t('breakTime')}</small>
          <span>{formatCalendarTimeRange(formatTime(calendar.break_start_time), formatTime(calendar.break_end_time), calendar.spans_next_day && calendar.break_end_time < calendar.check_in_time, undefined, t('nextDay'), ' - ')}</span>
        </div>

        <div className="mb-2">
          <small className="text-muted d-block">{t('workingDays')}</small>
          <div>
            {parseWorkingDays(calendar.working_days).map((day, index) => (
              <Badge key={index} bg="secondary" className="me-1 mb-1">
                {translateDay(day)}
              </Badge>
            ))}
          </div>
        </div>

        <div className="mb-2">
          <small className="text-muted d-block">{t('minDetections')}</small>
          <span>{calendar.min_detections || 1}</span>
        </div>
        
        <div className="d-flex justify-content-between align-items-center">
          <small className="text-muted">
            {tc('labels.created')}: {formatDateTime(calendar.created_at)}
          </small>
        </div>
      </Card.Body>
    </Card>
  );
});

CalendarCard.displayName = 'CalendarCard';

// Memoized Table Row Component
const CalendarTableRow = memo(({ 
  calendar, 
  onEdit, 
  companyTimezone,
  canEdit
}: { 
  calendar: Calendar; 
  onEdit: (calendar: Calendar) => void; 
  companyTimezone: string;
  canEdit: boolean;
}) => {
  const { t } = useTranslation('pages', { keyPrefix: 'calendars' });
  const { t: tc } = useTranslation('common');
  const renderCellValue = useCallback((value: any) => {
    if (value === null || value === undefined || value === '') {
      return tc('messages.na');
    }
    return value;
  }, [tc]);

  const formatDateTime = useCallback((dateString: string) => {
    if (!dateString) return tc('messages.na');
    return formatDateTimeInCompanyTimezone(dateString, companyTimezone);
  }, [companyTimezone, tc]);

  const formatTime = useCallback((timeString: string) => {
    if (!timeString) return tc('messages.na');
    return timeString.length > 5 ? timeString.substring(0, 5) : timeString;
  }, [tc]);

  const DAY_KEY_MAP: Record<string, string> = useMemo(() => ({
    Sunday: 'sunday', Monday: 'monday', Tuesday: 'tuesday', Wednesday: 'wednesday',
    Thursday: 'thursday', Friday: 'friday', Saturday: 'saturday',
  }), []);

  const translateDay = useCallback((day: string) => t(DAY_KEY_MAP[day] || day), [t, DAY_KEY_MAP]);

  const parseWorkingDays = useCallback((workingDays: string | string[]): string[] => {
    if (Array.isArray(workingDays)) {
      return workingDays;
    }
    if (typeof workingDays === 'string') {
      try {
        const parsed = JSON.parse(workingDays);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  return (
    <tr>
      <td>
        <div className="d-flex align-items-center">
          <CalendarIcon />
          <span className="fw-medium">{renderCellValue(calendar.name)}</span>
        </div>
      </td>
      <td><code className="small">{renderCellValue(calendar.company_id)}</code></td>
      <td>{formatCalendarTimeRange(formatTime(calendar.check_in_time), formatTime(calendar.check_out_time), calendar.spans_next_day, undefined, t('nextDay'), ' - ')}</td>
      <td>{formatCalendarTimeRange(formatTime(calendar.break_start_time), formatTime(calendar.break_end_time), calendar.spans_next_day && calendar.break_end_time < calendar.check_in_time, undefined, t('nextDay'), ' - ')}</td>
      <td>
        <div>
          {parseWorkingDays(calendar.working_days).map((day, index) => (
            <Badge key={index} bg="secondary" className="me-1 mb-1 small">
              {translateDay(day)}
            </Badge>
          ))}
        </div>
      </td>
      <td>
        <small>{calendar.grace_period_minutes}m / {calendar.late_threshold_minutes}m / {calendar.half_day_threshold_minutes}m / {t('minDetections')}: {calendar.min_detections || 1}</small>
      </td>
      <td>
        <small className="text-muted">
          {formatDateTime(calendar.created_at)}
        </small>
      </td>
      <td>
        {canEdit && (
          <Button variant="link" size="sm" className="text-primary p-1 text-decoration-none" onClick={() => onEdit(calendar)} title={t('editCalendar')}><i className="bi bi-pencil"></i> <span className="ms-1">{tc('buttons.edit')}</span></Button>
        )}
      </td>
    </tr>
  );
});

CalendarTableRow.displayName = 'CalendarTableRow';

const Calendars: React.FC = () => {
  const { t } = useTranslation('pages', { keyPrefix: 'calendars' });
  const { t: tc } = useTranslation('common');
  const { currentCompany } = useAuth();
  const { canAdd, canEdit } = useRolePermissions();
  
  // Get company timezone with fallback
  const companyTimezone = currentCompany?.timezone || 'UTC';
  
  const [calendars, setCalendars] = useState<Calendar[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [modalSuccess, setModalSuccess] = useState<string | null>(null);
  
  // Edit states
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingCalendar, setEditingCalendar] = useState<Calendar | null>(null);
  
  // Form states
  const [formData, setFormData] = useState<CreateCalendarRequest>({
    name: '',
    company_id: currentCompany?.company_id || '',
    check_in_time: '08:00',
    check_out_time: '16:00',
    break_start_time: '12:00',
    break_end_time: '13:00',
    grace_period_minutes: 10,
    late_threshold_minutes: 15,
    half_day_threshold_minutes: 240,
    min_detections: 1,
    working_days: ['Sunday']
  });

  // Available working days for selection
  const availableWorkingDays = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const DAY_KEY_MAP: Record<string, string> = useMemo(() => ({
    Sunday: 'sunday', Monday: 'monday', Tuesday: 'tuesday', Wednesday: 'wednesday',
    Thursday: 'thursday', Friday: 'friday', Saturday: 'saturday',
  }), []);

  const translateDay = useCallback((day: string) => t(DAY_KEY_MAP[day] || day), [t, DAY_KEY_MAP]);

  const fetchCalendars = useCallback(async () => {
    if (!currentCompany) return;

    try {
      setLoading(true);
      setError(null);
      
      const response = await calendarAPI.getCalendars(currentCompany.company_id);
      setCalendars(response.calendars || []);
    } catch (error: any) {
      console.error('Error fetching calendars:', error);
      setError(error.response?.data?.message || error.message || t('fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [currentCompany]);

  useEffect(() => {
    fetchCalendars();
  }, [fetchCalendars]);

  // Update company_id in form when currentCompany changes
  useEffect(() => {
    if (currentCompany) {
      setFormData(prev => ({ ...prev, company_id: currentCompany.company_id }));
    }
  }, [currentCompany]);

  // Helper function to parse working days
  const parseWorkingDays = useCallback((workingDays: string | string[]): string[] => {
    if (Array.isArray(workingDays)) {
      return workingDays;
    }
    if (typeof workingDays === 'string') {
      try {
        const parsed = JSON.parse(workingDays);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  }, []);

  // Memoized filtered calendars to prevent unnecessary re-computations
  const filteredCalendars = useMemo(() => {
    if (!searchTerm.trim()) return calendars;
    
    const searchLower = searchTerm.toLowerCase();
    return calendars.filter(calendar => {
      const workingDays = parseWorkingDays(calendar.working_days);
      return (
        calendar.name?.toLowerCase().includes(searchLower) ||
        calendar.company_id?.toLowerCase().includes(searchLower) ||
        workingDays.some(day => day.toLowerCase().includes(searchLower))
      );
    });
  }, [calendars, searchTerm, parseWorkingDays]);

  const totalPages = Math.ceil(filteredCalendars.length / rowsPerPage);
  const paginatedCalendars = useMemo(() => {
    const startIndex = (page - 1) * rowsPerPage;
    return filteredCalendars.slice(startIndex, startIndex + rowsPerPage);
  }, [filteredCalendars, page, rowsPerPage]);

  const handlePageChange = useCallback((newPage: number) => {
    setPage(newPage);
  }, []);

  const handleItemsPerPageChange = useCallback((newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1); // Reset to first page when changing items per page
  }, []);

  const handleSearchChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  }, []);

  const clearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  // Modal handlers
  const handleShowModal = useCallback(() => {
    setIsEditMode(false);
    setEditingCalendar(null);
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
  }, []);

  const handleEditCalendar = useCallback((calendar: Calendar) => {
    setIsEditMode(true);
    setEditingCalendar(calendar);
    setShowModal(true);
    setModalError(null);
    setModalSuccess(null);
    
    // Parse working days from string if necessary
    const parseWorkingDays = (workingDays: string | string[]): string[] => {
      if (Array.isArray(workingDays)) {
        return workingDays;
      }
      if (typeof workingDays === 'string') {
        try {
          const parsed = JSON.parse(workingDays);
          return Array.isArray(parsed) ? parsed : [];
        } catch {
          return [];
        }
      }
      return [];
    };
    
    const formatTimeForInput = (time: string): string => {
      if (!time) return '';
      return time.length > 5 ? time.substring(0, 5) : time;
    };
    
    setFormData({
      name: calendar.name || '',
      company_id: calendar.company_id || '',
      check_in_time: formatTimeForInput(calendar.check_in_time) || '08:00',
      check_out_time: formatTimeForInput(calendar.check_out_time) || '16:00',
      break_start_time: formatTimeForInput(calendar.break_start_time) || '12:00',
      break_end_time: formatTimeForInput(calendar.break_end_time) || '13:00',
      grace_period_minutes: calendar.grace_period_minutes || 10,
      late_threshold_minutes: calendar.late_threshold_minutes || 15,
      half_day_threshold_minutes: calendar.half_day_threshold_minutes || 240,
      min_detections: calendar.min_detections || 1,
      working_days: parseWorkingDays(calendar.working_days)
    });
  }, []);



  const handleCloseModal = useCallback(() => {
    setShowModal(false);
    setModalError(null);
    setModalSuccess(null);
    setIsEditMode(false);
    setEditingCalendar(null);
    setFormData({
      name: '',
      company_id: currentCompany?.company_id || '',
      check_in_time: '08:00',
      check_out_time: '16:00',
      break_start_time: '12:00',
      break_end_time: '13:00',
      grace_period_minutes: 10,
      late_threshold_minutes: 15,
      half_day_threshold_minutes: 240,
      min_detections: 1,
      working_days: ['Sunday']
    });
  }, [currentCompany]);

  const handleFormChange = useCallback((field: keyof CreateCalendarRequest, value: string | number | string[]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleWorkingDayToggle = useCallback((day: string) => {
    setFormData(prev => ({
      ...prev,
      working_days: prev.working_days.includes(day)
        ? prev.working_days.filter(d => d !== day)
        : [...prev.working_days, day]
    }));
  }, []);

  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentCompany) return;

    try {
      setModalLoading(true);
      setModalError(null);
      setModalSuccess(null);

      if (isEditMode && editingCalendar) {
        const updateData: UpdateCalendarRequest = {
          name: formData.name,
          check_in_time: formData.check_in_time,
          check_out_time: formData.check_out_time,
          break_start_time: formData.break_start_time,
          break_end_time: formData.break_end_time,
          grace_period_minutes: formData.grace_period_minutes,
          late_threshold_minutes: formData.late_threshold_minutes,
          half_day_threshold_minutes: formData.half_day_threshold_minutes,
          min_detections: formData.min_detections || 1,
          working_days: formData.working_days
        };
        await calendarAPI.updateCalendar(editingCalendar.id, updateData, currentCompany.company_id);
        setModalSuccess(t('updateSuccess'));
      } else {
        await calendarAPI.createCalendar(formData);
        setModalSuccess(t('createSuccess'));
      }
      
      // Refresh the list
      await fetchCalendars();
      
      // Close modal after a short delay
      setTimeout(() => {
        handleCloseModal();
      }, 2000);
      
    } catch (error: any) {
      console.error(`Error ${isEditMode ? 'updating' : 'creating'} calendar:`, error);
      setModalError(error.response?.data?.message || error.message || (isEditMode ? t('updateFailed') : t('createFailed')));
    } finally {
      setModalLoading(false);
    }
  }, [formData, currentCompany, fetchCalendars, handleCloseModal, isEditMode, editingCalendar, t]);

  // Generate pagination items
  const renderPaginationItems = useCallback(() => {
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    for (let i = startPage; i <= endPage; i++) {
      items.push(
        <Pagination.Item
          key={i}
          active={i === page}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return items;
  }, [page, totalPages, handlePageChange]);

  // Force cache clear when company changes
  useEffect(() => {
    if (currentCompany) {
      clearCache(`calendars_${currentCompany.company_id}`);
      setSearchTerm('');
      setPage(1);
    }
  }, [currentCompany]);

  return (
    <Layout title={t('title')}>
      <Container fluid className="an-enterprise-page an-page-shell an-data-page">
        <div className="page-header">
          <h2 className="an-page-title">
            {t('title')}
          </h2>
          {currentCompany && canAdd('calendars') && (
            <Button variant="primary" onClick={handleShowModal}>
              <i className="bi bi-plus-lg"></i>
              {t('addCalendar')}
            </Button>
          )}
        </div>
        
        {currentCompany && (
          <Alert variant="info" className="mb-4">
            <strong>{tc('messages.showingFor', { entity: t('title').toLowerCase() })}</strong> {currentCompany.description} (ID: {currentCompany.company_id})
          </Alert>
        )}

        {!currentCompany && (
          <Alert variant="warning" className="mb-4">
            {tc('messages.selectCompany', { entity: t('title').toLowerCase() })}
          </Alert>
        )}

        {error && (
          <Alert variant="danger" className="mb-4">
            {error}
          </Alert>
        )}

        {/* Search Bar */}
        {currentCompany && (
          <Card className="mb-4 an-data-card">
            <Card.Body className="an-data-card">
              <InputGroup>
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder={t('searchPlaceholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                />
                {searchTerm && (
                  <Button variant="outline-secondary" onClick={clearSearch}>
                    <i className="bi bi-x-lg"></i>
                  </Button>
                )}
              </InputGroup>
              {searchTerm && (
                <small className="text-muted mt-2 d-block">
                  {t('found', { count: filteredCalendars.length, term: searchTerm })}
                </small>
              )}
            </Card.Body>
          </Card>
        )}

        {loading && (
          <div className="d-flex justify-content-center align-items-center py-5">
            <Spinner animation="border" variant="primary" className="me-3" />
            <span>{t('loading')}</span>
          </div>
        )}

        {currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="p-0 an-data-card">
              {/* Mobile Card View */}
              <div className="d-md-none">
                {paginatedCalendars.length > 0 ? (
                  paginatedCalendars.map((calendar) => (
                    <CalendarCard
                      key={calendar.id}
                      calendar={calendar}
                      onEdit={handleEditCalendar}
                      canEdit={canEdit('calendars')}
                    />
                  ))
                ) : (
                  <div className="text-center py-5">
                    <CalendarIcon />
                    <h5>{t('noTitle')}</h5>
                    <p className="text-muted">
                      {searchTerm 
                        ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                        : t('noneSetUp', { company: currentCompany.description })
                      }
                    </p>
                    {canAdd('calendars') && (
                      <Button variant="primary" onClick={handleShowModal}>
                        <i className="bi bi-plus-lg"></i>
                        {t('addFirstCalendar')}
                      </Button>
                    )}
                  </div>
                )}
              </div>

              {/* Desktop Table View */}
              <div className="d-none d-md-block">
                <div className="table-responsive an-data-table an-panel">
                  <Table hover className="mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>{t('calendarName')}</th>
                        <th>{t('companyIdColumn')}</th>
                        <th>{t('workHours')}</th>
                        <th>{t('breakTime')}</th>
                        <th>{t('workingDays')}</th>
                        <th>{t('thresholds')}</th>
                        <th>{tc('labels.created')}</th>
                        <th>{tc('labels.actions')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedCalendars.length > 0 ? (
                        paginatedCalendars.map((calendar) => (
                          <CalendarTableRow
                            key={calendar.id}
                            calendar={calendar}
                            onEdit={handleEditCalendar}
                            companyTimezone={companyTimezone}
                            canEdit={canEdit('calendars')}
                          />
                        ))
                      ) : (
                        <tr>
                          <td colSpan={8} className="text-center py-5">
                            <CalendarIcon />
                            <h5>{t('noTitle')}</h5>
                            <p className="text-muted mb-3">
                              {searchTerm 
                                ? t('noMatch', { term: searchTerm, company: currentCompany.description })
                                : t('noneSetUp', { company: currentCompany.description })
                              }
                            </p>
                            {canAdd('calendars') && (
                              <Button variant="primary" onClick={handleShowModal}>
                                <i className="bi bi-plus-lg"></i>
                                {t('addFirstCalendar')}
                              </Button>
                            )}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>

              {/* Pagination */}
              {filteredCalendars.length > rowsPerPage && (
                <div className="d-flex justify-content-between align-items-center p-3 border-top">
                  <span className="text-muted small">
                    {tc('messages.showing', { from: ((page - 1) * rowsPerPage) + 1, to: Math.min(page * rowsPerPage, filteredCalendars.length), total: filteredCalendars.length, entity: t('title').toLowerCase() })}
                  </span>
                  <Pagination className="mb-0 an-pagination">
                    <Pagination.Prev 
                      disabled={page === 1}
                      onClick={() => handlePageChange(page - 1)}
                    />
                    {renderPaginationItems()}
                    <Pagination.Next 
                      disabled={page === totalPages}
                      onClick={() => handlePageChange(page + 1)}
                    />
                  </Pagination>
                </div>
              )}

              {/* Items per page selector */}
              <Row className="mt-3 mb-4">
                <Col className="d-flex justify-content-center align-items-center gap-2">
                  <span className="text-muted small">{tc('messages.itemsPerPage')}</span>
                  <Form.Select
                    size="sm"
                    style={{ width: 'auto' }}
                    value={rowsPerPage}
                    onChange={(e) => handleItemsPerPageChange(Number(e.target.value))}
                  >
                    <option value={10}>10</option>
                    <option value={20}>20</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </Form.Select>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        )}

        {!currentCompany && !loading && (
          <Card className="an-data-card">
            <Card.Body className="text-center py-5 an-data-card">
              <h5>{tc('messages.selectCompanyTitle')}</h5>
              <p className="text-muted">
                {tc('messages.selectCompanyBody', { entity: t('title').toLowerCase() })}
              </p>
            </Card.Body>
          </Card>
        )}

        {/* Create Calendar Modal */}
        <Modal show={showModal} onHide={handleCloseModal} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>{isEditMode ? t('editTitle') : t('addTitle')}</Modal.Title>
          </Modal.Header>
          <Form onSubmit={handleSubmit}>
            <Modal.Body>
              {modalSuccess && (
                <Alert variant="success" className="mb-3">
                  {modalSuccess}
                </Alert>
              )}
              
              {modalError && (
                <Alert variant="danger" className="mb-3">
                  {modalError}
                </Alert>
              )}

              <Row>
                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('nameLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => handleFormChange('name', e.target.value)}
                      placeholder={t('namePlaceholder')}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('companyIdLabel')}</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.company_id}
                      onChange={(e) => handleFormChange('company_id', e.target.value)}
                      placeholder={t('companyIdPlaceholder')}
                      required
                      readOnly
                      className="bg-light"
                    />
                    {isEditMode && (
                      <Form.Text className="text-muted">
                        {t('companyIdEditHint')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('checkInTimeLabel')}</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.check_in_time}
                      onChange={(e) => handleFormChange('check_in_time', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('checkOutTimeLabel')}</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.check_out_time}
                      onChange={(e) => handleFormChange('check_out_time', e.target.value)}
                      required
                    />
                  </Form.Group>
                </Col>

                {formData.check_out_time && formData.check_in_time && formData.check_out_time < formData.check_in_time && (
                  <Col xs={12} className="mb-3">
                    <Alert variant="info" className="py-2 mb-0">
                      <i className="bi bi-moon-stars me-2"></i>
                      {t('overnightShiftHint')}
                    </Alert>
                  </Col>
                )}

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('breakStartLabel')}</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.break_start_time}
                      onChange={(e) => handleFormChange('break_start_time', e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('breakEndLabel')}</Form.Label>
                    <Form.Control
                      type="time"
                      value={formData.break_end_time}
                      onChange={(e) => handleFormChange('break_end_time', e.target.value)}
                    />
                    {formData.break_end_time && formData.check_in_time && formData.break_end_time < formData.check_in_time && (
                      <Form.Text className="text-muted">
                        {t('overnightBreakHint')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('gracePeriodLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.grace_period_minutes}
                      onChange={(e) => handleFormChange('grace_period_minutes', parseInt(e.target.value))}
                      min="0"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('lateThresholdLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.late_threshold_minutes}
                      onChange={(e) => handleFormChange('late_threshold_minutes', parseInt(e.target.value))}
                      min="0"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('halfDayThresholdLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.half_day_threshold_minutes}
                      onChange={(e) => handleFormChange('half_day_threshold_minutes', parseInt(e.target.value))}
                      min="0"
                      required
                    />
                  </Form.Group>
                </Col>

                <Col md={4} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('minDetectionsLabel')}</Form.Label>
                    <Form.Control
                      type="number"
                      value={formData.min_detections || 1}
                      onChange={(e) => handleFormChange('min_detections', Math.max(1, parseInt(e.target.value) || 1))}
                      min="1"
                      max="50"
                      required
                    />
                    <Form.Text className="text-muted">{t('minDetectionsHint')}</Form.Text>
                  </Form.Group>
                </Col>

                <Col md={12} className="mb-3">
                  <Form.Group>
                    <Form.Label>{t('workingDaysLabel')}</Form.Label>
                    <div>
                      {availableWorkingDays.map((day) => (
                        <Form.Check
                          key={day}
                          inline
                          type="checkbox"
                          id={`working-day-${day}`}
                          label={translateDay(day)}
                          checked={formData.working_days.includes(day)}
                          onChange={() => handleWorkingDayToggle(day)}
                        />
                      ))}
                    </div>
                    {formData.working_days.length === 0 && (
                      <Form.Text className="text-danger">
                        {t('selectWorkingDay')}
                      </Form.Text>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={handleCloseModal} disabled={modalLoading}>
                {tc('buttons.cancel')}
              </Button>
              <Button 
                variant="primary" 
                type="submit" 
                disabled={modalLoading || formData.working_days.length === 0}
              >
                {modalLoading ? (
                  <>
                    <Spinner animation="border" size="sm" className="me-2" />
                    {isEditMode ? tc('loading.updating') : tc('loading.creating')}
                  </>
                ) : (
                  <>
                    {isEditMode ? <i className="bi bi-pencil"></i> : <i className="bi bi-plus-lg"></i>}
                    {isEditMode ? t('updateCalendar') : t('createCalendar')}
                  </>
                )}
              </Button>
            </Modal.Footer>
          </Form>
        </Modal>
      </Container>
    </Layout>
  );
};

export default Calendars; 


